import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { RichTextDisplay } from "@/components/RichTextEditor";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronLeft, Edit, Users, Skull, Scroll, Sparkles, Castle, Map, ShieldCheck, Package, Sword, Shield, FlaskConical, Gem, Crown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENTITY_CONFIG = {
  character: {
    icon: Users,
    endpoint: "characters",
    label: "Character",
    color: "text-primary"
  },
  creature: {
    icon: Skull,
    endpoint: "creatures",
    label: "Creature",
    color: "text-secondary"
  },
  lore: {
    icon: Scroll,
    endpoint: "lore",
    label: "Lore",
    color: "text-primary"
  },
  deity: {
    icon: Sparkles,
    endpoint: "deities",
    label: "Deity",
    color: "text-primary"
  },
  location: {
    icon: Castle,
    endpoint: "locations",
    label: "Location",
    color: "text-primary"
  },
  map: {
    icon: Map,
    endpoint: "maps",
    label: "Map",
    color: "text-primary"
  },
  item: {
    icon: Package,
    endpoint: "items",
    label: "Item",
    color: "text-primary"
  }
};

const EntityDetailPage = () => {
  const { campaignId, entityType, entityId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedData, setRelatedData] = useState({});
  const [characterItems, setCharacterItems] = useState([]);

  const config = ENTITY_CONFIG[entityType];

  useEffect(() => {
    if (!config) {
      navigate(`/campaign/${campaignId}`);
      return;
    }
    fetchData();
  }, [campaignId, entityType, entityId]);

  const fetchData = async () => {
    try {
      const [campaignRes, entityRes] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true }),
        axios.get(`${API}/${config.endpoint}/${entityId}`, { withCredentials: true })
      ]);
      setCampaign(campaignRes.data);
      setEntity(entityRes.data);

      // Fetch related data based on entity type
      if (entityType === "character") {
        const promises = [];
        if (entityRes.data.location_id) {
          promises.push(
            axios.get(`${API}/locations/${entityRes.data.location_id}`, { withCredentials: true })
              .then(r => ({ location: r.data }))
              .catch(() => ({}))
          );
        }
        // Fetch items held by this character
        promises.push(
          axios.get(`${API}/characters/${entityId}/items`, { withCredentials: true })
            .then(r => { setCharacterItems(r.data); return {}; })
            .catch(() => { setCharacterItems([]); return {}; })
        );
        const results = await Promise.all(promises);
        const merged = results.reduce((acc, r) => ({ ...acc, ...r }), {});
        setRelatedData(merged);
      }
    } catch (error) {
      toast.error("Failed to load data");
      navigate(`/campaign/${campaignId}`);
    } finally {
      setLoading(false);
    }
  };

  const getBackPath = () => {
    const paths = {
      character: "characters",
      creature: "creatures",
      lore: "lore",
      deity: "deities",
      location: "locations",
      map: "maps",
      item: "items"
    };
    return `/campaign/${campaignId}/${paths[entityType]}`;
  };

  if (loading || !config) {
    return (
      <Layout campaignName="Loading...">
        <div className="flex justify-center py-20">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  const getEntityImageUrl = () => {
    if (entity?.image_path) {
      return entity.image_path.startsWith("http") ? entity.image_path : `${API}/files/${entity.image_path}`;
    }
    return null;
  };

  const Icon = config.icon;

  const ITEM_TYPE_ICONS = {
    weapon: Sword, armour: Shield, armor: Shield, potion: FlaskConical,
    scroll: Scroll, gem: Gem, artifact: Crown, misc: Package,
    gear: Package, wondrous: Sparkles, ring: Gem
  };
  const RARITY_COLORS = {
    common: "text-stone-400", uncommon: "text-green-400", rare: "text-blue-400",
    very_rare: "text-violet-400", legendary: "text-amber-400"
  };

  const equippedItems = characterItems.filter(i => i.equipped);
  const packItems = characterItems.filter(i => !i.equipped);

  const renderCharacterDetails = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <InfoCard label="Type" value={entity.character_type === "pc" ? "Player Character" : "NPC"} />
        <InfoCard label="Race" value={entity.race} />
        <InfoCard label="Class" value={entity.character_class} />
        <InfoCard label="Level" value={entity.level} />
      </div>
      {relatedData.location && (
        <div>
          <p className="text-sm text-muted-foreground mb-2">Location</p>
          <Link 
            to={`/campaign/${campaignId}/location/${relatedData.location.id}`}
            className="text-primary hover:underline"
          >
            {relatedData.location.name}
          </Link>
        </div>
      )}

      {/* Equipped Items Section */}
      {characterItems.length > 0 && (
        <div data-testid="character-items-section">
          {equippedItems.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-serif text-foreground mb-3 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" /> Equipped
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {equippedItems.map(item => {
                  const ItemIcon = ITEM_TYPE_ICONS[item.item_type] || Package;
                  const rarityColor = RARITY_COLORS[item.rarity] || "text-stone-400";
                  return (
                    <div key={item.id} className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-sm px-3 py-2" data-testid={`equipped-item-${item.id}`}>
                      <ItemIcon className="w-4 h-4 text-primary flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{item.name}</p>
                        <p className={`text-xs ${rarityColor}`}>{(item.rarity || "common").replace("_", " ")}</p>
                      </div>
                      {item.properties && <p className="text-[10px] text-muted-foreground font-mono hidden sm:block max-w-[120px] truncate">{item.properties}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {packItems.length > 0 && (
            <div>
              <h3 className="text-lg font-serif text-foreground mb-3 flex items-center gap-2">
                <Package className="w-5 h-5 text-muted-foreground" /> Pack
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {packItems.map(item => {
                  const ItemIcon = ITEM_TYPE_ICONS[item.item_type] || Package;
                  const rarityColor = RARITY_COLORS[item.rarity] || "text-stone-400";
                  return (
                    <div key={item.id} className="flex items-center gap-3 bg-muted/20 border border-border rounded-sm px-3 py-2" data-testid={`pack-item-${item.id}`}>
                      <ItemIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-foreground truncate">{item.name}</p>
                        <p className={`text-xs ${rarityColor}`}>{(item.rarity || "common").replace("_", " ")}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const abilityMod = (score) => {
    const mod = Math.floor(((score || 10) - 10) / 2);
    return mod >= 0 ? `+${mod}` : `${mod}`;
  };

  const StatBlockEntries = ({ title, items }) => {
    if (!items || items.length === 0) return null;
    return (
      <div>
        <h4 className="text-sm font-bold text-primary border-b border-primary/30 pb-1 mb-2">{title}</h4>
        {items.map((item, i) => (
          <p key={i} className="text-sm mb-1.5"><strong className="text-foreground">{item.name}.</strong> <span className="text-muted-foreground">{item.description}</span></p>
        ))}
      </div>
    );
  };

  const renderCreatureDetails = () => (
    <div className="space-y-4" data-testid="creature-stat-block">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <InfoCard label="Type" value={entity.creature_type} />
        <InfoCard label="Size" value={entity.size} />
        <InfoCard label="Alignment" value={entity.alignment} />
        <InfoCard label="Challenge Rating" value={entity.challenge_rating ? `CR ${entity.challenge_rating}` : null} />
      </div>

      {(entity.armor_class || entity.hit_points || entity.speed) && (
        <div className="grid grid-cols-3 gap-4">
          <InfoCard label="Armour Class" value={entity.armor_class} />
          <InfoCard label="Hit Points" value={entity.hit_points} />
          <InfoCard label="Speed" value={entity.speed} />
        </div>
      )}

      {(entity.str_score && (entity.str_score !== 10 || entity.dex_score !== 10 || entity.con_score !== 10 || entity.int_score !== 10 || entity.wis_score !== 10 || entity.cha_score !== 10)) && (
        <div className="bg-muted/20 rounded-sm p-3">
          <div className="grid grid-cols-6 gap-2 text-center">
            {[["STR", entity.str_score], ["DEX", entity.dex_score], ["CON", entity.con_score], ["INT", entity.int_score], ["WIS", entity.wis_score], ["CHA", entity.cha_score]].map(([label, score]) => (
              <div key={label}>
                <p className="text-[10px] font-bold uppercase text-muted-foreground">{label}</p>
                <p className="text-lg font-bold text-foreground">{score || 10}</p>
                <p className="text-xs text-muted-foreground">({abilityMod(score)})</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-1 text-sm">
        {entity.saving_throws && <p><strong className="text-foreground">Saving Throws</strong> <span className="text-muted-foreground">{entity.saving_throws}</span></p>}
        {entity.skills && <p><strong className="text-foreground">Skills</strong> <span className="text-muted-foreground">{entity.skills}</span></p>}
        {entity.damage_resistances && <p><strong className="text-foreground">Damage Resistances</strong> <span className="text-muted-foreground">{entity.damage_resistances}</span></p>}
        {entity.damage_immunities && <p><strong className="text-foreground">Damage Immunities</strong> <span className="text-muted-foreground">{entity.damage_immunities}</span></p>}
        {entity.condition_immunities && <p><strong className="text-foreground">Condition Immunities</strong> <span className="text-muted-foreground">{entity.condition_immunities}</span></p>}
        {entity.senses && <p><strong className="text-foreground">Senses</strong> <span className="text-muted-foreground">{entity.senses}</span></p>}
        {entity.languages && <p><strong className="text-foreground">Languages</strong> <span className="text-muted-foreground">{entity.languages}</span></p>}
      </div>

      <StatBlockEntries title="Traits" items={entity.traits} />
      <StatBlockEntries title="Actions" items={entity.actions} />
      <StatBlockEntries title="Reactions" items={entity.reactions} />
      {entity.legendary_actions?.length > 0 && (
        <div>
          <h4 className="text-sm font-bold text-primary border-b border-primary/30 pb-1 mb-2">Legendary Actions</h4>
          {entity.legendary_description && <p className="text-sm text-muted-foreground mb-2 italic">{entity.legendary_description}</p>}
          {entity.legendary_actions.map((item, i) => (
            <p key={i} className="text-sm mb-1.5"><strong className="text-foreground">{item.name}.</strong> <span className="text-muted-foreground">{item.description}</span></p>
          ))}
        </div>
      )}
    </div>
  );

  const renderLoreDetails = () => (
    <div>
      <InfoCard label="Category" value={entity.category} className="capitalize" />
    </div>
  );

  const RARITY_LABELS = { common: "Common", uncommon: "Uncommon", rare: "Rare", very_rare: "Very Rare", legendary: "Legendary", artifact: "Artifact" };

  const renderItemDetails = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <InfoCard label="Type" value={entity.item_type} className="capitalize" />
        <InfoCard label="Rarity" value={RARITY_LABELS[entity.rarity] || entity.rarity} />
        <InfoCard label="Properties" value={entity.properties} />
      </div>
      {entity.equipped && (
        <div className="flex items-center gap-2 text-sm text-primary">
          <ShieldCheck className="w-4 h-4" /> Currently Equipped
        </div>
      )}
    </div>
  );

  const renderDeityDetails = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      <InfoCard label="Domain" value={entity.domain} />
      <InfoCard label="Alignment" value={entity.alignment} />
      <InfoCard label="Symbol" value={entity.symbol} />
    </div>
  );

  const renderLocationDetails = () => (
    <div className="grid grid-cols-2 gap-4">
      <InfoCard label="Type" value={entity.location_type} className="capitalize" />
    </div>
  );

  const renderDetails = () => {
    switch (entityType) {
      case "character": return renderCharacterDetails();
      case "creature": return renderCreatureDetails();
      case "lore": return renderLoreDetails();
      case "deity": return renderDeityDetails();
      case "location": return renderLocationDetails();
      case "item": return renderItemDetails();
      default: return null;
    }
  };

  return (
    <Layout campaignName={campaign?.name} tabLabel={entity.name || entity.title}>
      <div data-testid="entity-detail-page">
        {/* Back button */}
        <Button
          variant="ghost"
          className="mb-4 text-muted-foreground hover:text-foreground"
          onClick={() => navigate(getBackPath())}
          data-testid="back-button"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to {config.label}s
        </Button>

        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-6">
            {/* Header */}
            <div className="flex items-start gap-4 mb-6">
              {getEntityImageUrl() ? (
                <div className="w-20 h-20 rounded-sm overflow-hidden flex-shrink-0">
                  <img
                    src={getEntityImageUrl()}
                    alt={entity.name || entity.title}
                    className="w-full h-full object-cover"
                    {...(!(entity.image_path?.startsWith("http")) && { crossOrigin: "use-credentials" })}
                    data-testid="entity-detail-image"
                  />
                </div>
              ) : (
                <div className={`w-14 h-14 rounded-sm bg-primary/10 flex items-center justify-center`}>
                  <Icon className={`w-8 h-8 ${config.color}`} />
                </div>
              )}
              <div className="flex-1">
                <h1 className="text-3xl font-serif text-foreground">{entity.name || entity.title}</h1>
                <p className="text-sm text-muted-foreground">{config.label}</p>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate(getBackPath())}
                data-testid="edit-entity-button"
                className="border-border"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </div>

            {/* Entity-specific details */}
            <div className="mb-8">
              {renderDetails()}
            </div>

            {/* Description */}
            <div>
              <h3 className="text-lg font-serif text-foreground mb-4">Description</h3>
              {entity.description || entity.content ? (
                <RichTextDisplay content={entity.description || entity.content} />
              ) : (
                <p className="text-muted-foreground italic">No description available.</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

const InfoCard = ({ label, value, className = "" }) => {
  if (!value) return null;
  return (
    <div className="bg-muted/20 p-3 rounded-sm">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className={`text-foreground font-medium ${className}`}>{value}</p>
    </div>
  );
};

export default EntityDetailPage;
